/**
 * This is intended to allow external code (such as the rules engine) the ability to
 * add or remove DynamicFieldEntryRow entries.
 * For example, if a user requests a location field, this code will be used to
 * add building and company as well since locations are useless by themselves.
 * Pulling this into a hook is intended to simplify reading and understanding the
 * core component code by removing these edge case distractions.
 */
import { useEffect, useState } from 'react';
import useEventSink from '../../../hooks/useEventSink';

export default function useExternalFieldAddAndRemoveRequests(setEntries, setAllowedEntries, entriesReady) {
    const [subscribe] = useEventSink();

    // Add queued DynamicFieldRow entries and remove them from the allowed entries (so they
    // won't be reused).
    const [queuedAdds, setQueuedAdds] = useState([]);
    useEffect(() => {
        if (queuedAdds.length > 0 && entriesReady) {
            // Update entries first
            setEntries(prev => {
                const newArray = [...prev];
                newArray.push(...queuedAdds);
                return newArray;
            });

            // Then update allowed entries
            setAllowedEntries(prev => {
                const newValues = [...prev];
                queuedAdds.forEach(queuedAdd => {
                    // If we are adding an hNode as an entry, remove it from the allowed entries
                    if (queuedAdd.hNodeType != null && prev.some(p => p.id === queuedAdd.id)) {
                        newValues.splice(
                            newValues.findIndex(n => n.id === queuedAdd.id),
                            1
                        );
                    }
                });
                return newValues;
            });

            // Clear the queue
            setQueuedAdds([]);
        }
    }, [setEntries, setAllowedEntries, entriesReady, queuedAdds]);

    // Remove queued DynamicFieldRow entries and add them to the allowed entries (so they
    // can be reused).
    const [queuedRemoves, setQueuedRemoves] = useState([]);
    useEffect(() => {
        if (queuedRemoves.length > 0 && entriesReady) {
            const removeIds = queuedRemoves.map(queuedRemove => queuedRemove.id);
            
            // Update entries first
            setEntries(prev => {
                return prev.filter(entry => !removeIds.includes(entry.id));
            });

            // Then update allowed entries
            setAllowedEntries(prev => {
                return [...prev, ...queuedRemoves];
            });

            // Clear the queue
            setQueuedRemoves([]);
        }
    }, [setEntries, setAllowedEntries, entriesReady, queuedRemoves]);

    // Adding entries is an async operation, so we need to create a queue that will be aware
    // when the entries array is ready for more to be added.
    useEffect(() => {
        return subscribe({ verb: 'AddDynamicFieldEntry', namespace: 'application', relation: 'runtime' }, payload => {
            setQueuedAdds(prev => {
                return [...prev, payload];
            });
        });
    }, [subscribe]);

    // Removing entries is an async operation, so we need to create a queue that will be aware
    // when the entries array is ready for more to be removed.
    useEffect(() => {
        return subscribe(
            { verb: 'RemoveDynamicFieldEntry', namespace: 'application', relation: 'runtime' },
            payload => {
                setQueuedRemoves(prev => {
                    return [...prev, payload];
                });
            }
        );
    }, [subscribe]);
}
